/* AgentDock — the app's single agent surface: realtime voice + text chat
   (RAG with citations) in one dock.
   ──────────────────────────────────────────────────────────────
   Merges the former VoiceAgent (realtime WS voice) and ChatAgent
   (knowledge Q&A) widgets. Four views:

     collapsed  → floating launcher (bottom-right)
     panel      → standard dock
     expanded   → large workspace (~min(760px, 92vw) × 82vh)
     rail       → nav-collapsed slim right-edge strip: when the agent
                  navigates the app it gets out of the way but stays
                  hot (live voice state + quick mute; click restores)

   Shortcuts (capture phase): Cmd/Ctrl+K toggle · Esc collapse ·
   Cmd/Ctrl+Shift+V start/end voice · Cmd/Ctrl+Shift+M mute mic.

   Voice plumbing ported (genericized) from a production voice-agent
   kit. The proven parts are kept verbatim where possible:

     - PCM16 ↔ Float32 audio conversion + scheduled play queue
     - Realtime WebSocket lifecycle (session.update handshake, VAD
       events, barge-in cancel, transcript deltas, tool-call loop)
     - Frequency-bar visualiser driven by an AnalyserNode

   The flow:
     1. POST {API}/voice/session (Firebase auth) → ephemeral token +
        wsUrl/wsProtocols + session config. The real provider API key
        never reaches this code.
     2. Open a WebSocket DIRECTLY to the provider; stream mic PCM up,
        play PCM deltas back. Mic capture runs in an AudioWorklet
        (ScriptProcessor fallback for older browsers). An unexpected
        socket drop auto-reconnects with a fresh ephemeral token.
     3. Tool calls: client tools (navigate_app) run in the browser via
        react-router; everything else POSTs to {API}/voice/tool and the
        result is fed back into the conversation.

   Text chat: the panel also has a text input. During a live voice
   session typed messages are injected into the realtime conversation;
   otherwise they go to {API}/voice/chat (same tool registry, no audio).

   Playback-clock sync (ported from the ibkr kit): providers generate
   audio faster than it plays, so anything keyed to frame ARRIVAL —
   transcript deltas, navigation tool calls — runs seconds ahead of the
   audible voice. Everything user-visible is scheduled against the audio
   playhead instead (see playbackClock.js): the caption reveals as words
   are spoken, and auto-navigation fires when the narration that
   triggered it becomes audible. On barge-in only the text actually
   HEARD is kept.

   Mounted once from App.js for members (voice + chat) and, with
   REACT_APP_PUBLIC_CHAT=1, for signed-out visitors too (chat only,
   via anonymous auth — per-chunk audience gating limits them to
   'public' corpus content). Renders nothing unless
   REACT_APP_UPLOAD_API is configured. */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { citationHref, citationKind } from '../utils/citationLink';
import HowIKnew from './HowIKnew';
import BrainThink, { stageOf } from './BrainThink';
import { loadBrainGraph, THINK_HREF } from '../data/brainGraph';
import useCopilotTurn, { requestCopilotOpen, requestCopilotClose, COPILOT_STATE_EVENT } from '../hooks/useCopilotTurn';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import firebase from '../firebaseConfig';
import { isPg, getSupabase } from '../data/supabaseClient';
import { getIdToken } from '../data/authPlane';
import { scrollPage, safeSection } from './scrollPage';
import StageStrip from './StageStrip';
import { captureScreenOutline, screenContextItem } from '../utils/screenOutline';
import { runPageControl, isPageControlTool, DRIVING_TOOLS } from '../utils/pageControls';
import '../utils/pageControls.css';
import {
  configurePlaybackClock,
  noteAudioScheduled,
  scheduleTranscript,
  scheduleTranscriptCommit,
  scheduleDirective,
  watchDrain,
  clearPlaybackQueues,
  stopPlaybackClock,
} from './playbackClock';
import './AgentDock.css';
import { kitEnv } from '../data/kitEnv.js';
import { getActiveDevSession } from '../dev/liveDocs';
import useDevNarration from '../dev/useDevNarration';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');
const PUBLIC_CHAT = kitEnv('PUBLIC_CHAT', process.env.REACT_APP_PUBLIC_CHAT || '') === '1';
const IS_MAC = /Mac|iP(hone|ad|od)/.test(navigator.platform || '');
const MOD = IS_MAC ? '⌘' : 'Ctrl+';
const SAMPLE_RATE = 24000;
const NUM_BARS = 20;
const ATTACK = 0.12;
const DECAY = 0.45;
const MIC_CHUNK = 4096; // samples per chunk sent upstream (~170ms @ 24kHz)
const MAX_MIC_BUFFER = 120; // pre-handshake/reconnect buffer cap (~20s)
const MAX_RECONNECTS = 2;
// On /dev a session is the work itself: the token refreshes every ~5 min and
// a long coding session should ride through several of them.
const MAX_RECONNECTS_DEV = 12;
const maxReconnects = () => (window.location.pathname.startsWith('/dev') ? MAX_RECONNECTS_DEV : MAX_RECONNECTS);
const VOICE_SCREEN_CHARS = 1500; // outline budget per screen item (handles included)
const SCREEN_MIN_GAP_MS = 4000;  // speech-start refresh floor

/* Mic capture worklet — buffers MIC_CHUNK samples then posts them to the
   main thread. Loaded from a Blob URL so CRA doesn't need to serve a file. */
const MIC_WORKLET_SRC = `
registerProcessor('va-mic-capture', class extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buf = new Float32Array(${MIC_CHUNK});
    this.len = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    let off = 0;
    while (off < ch.length) {
      const n = Math.min(ch.length - off, this.buf.length - this.len);
      this.buf.set(ch.subarray(off, off + n), this.len);
      this.len += n;
      off += n;
      if (this.len === this.buf.length) {
        this.port.postMessage(this.buf.slice(0));
        this.len = 0;
      }
    }
    return true;
  }
});
`;

function float32ToBase64PCM16(f32) {
  const pcm16 = new Int16Array(f32.length);
  for (let i = 0; i < f32.length; i++) {
    const s = Math.max(-1, Math.min(1, f32[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm16.buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

async function authHeaders() {
  // Anonymous public-chat sign-in on either plane: Firebase anonymous auth |
  // GoTrue anonymous users (GOTRUE_EXTERNAL_ANONYMOUS_USERS_ENABLED, set by
  // provision for starter-site instances). The backend's audience gating
  // limits anonymous tokens to public corpus content and refuses voice.
  if (PUBLIC_CHAT) {
    if (isPg()) {
      const supabase = getSupabase();
      if (supabase && !(await supabase.auth.getSession()).data?.session) {
        await supabase.auth.signInAnonymously();
      }
    } else if (!firebase.auth().currentUser) {
      await firebase.auth().signInAnonymously();
    }
  }
  // Firebase ID token | Supabase access_token — the backend accepts both.
  const token = await getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/* ── Watch the brain think, from any page ([[watch-the-brain-think]]) ──
   The dock plays the turn's think stream beside the transcript: a live
   voice turn as each /voice/tool trace returns, a text turn when its trace
   lands. config/app voice.think.stage (via GET /voice/ui): 'auto' opens the
   theatre column on the first think event, 'pill' leaves the watch toggle,
   'off' hides it all. A person's own watch/hide outranks 'auto' for the
   session. */
const THINK_UI_DEFAULT = { stage: 'auto' };
const WATCH_KEY = 'nv.think.watch'; // session: 'on' | 'off'
const watchPref = () => { try { return sessionStorage.getItem(WATCH_KEY); } catch (_) { return null; } };
const setWatchPref = (v) => { try { sessionStorage.setItem(WATCH_KEY, v); } catch (_) { /* private mode */ } };
function useWide(minPx) {
  const q = `(min-width: ${minPx}px)`;
  const [wide, setWide] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : true));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    on();
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, [q]);
  return wide;
}

/* voiceEnabled: members get the full dock; public visitors (PUBLIC_CHAT)
   get chat only — every voice session mints a paid realtime-provider
   token, an abuse vector on a public page. */
export default function AgentDock({ voiceEnabled: voiceProp = true }) {
  const navigate = useNavigate();
  // Hosted instances carry the plane's entitlements on /license/status
  // (features.voice = the voice add-on). No add-on → no Start voice; an
  // unlicensed (dormant) dev build keeps voice. Fail open on any error.
  const [voiceLicensed, setVoiceLicensed] = useState(true);
  useEffect(() => {
    if (!voiceProp) return undefined;
    let alive = true;
    fetch(`${API}/license/status`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((lic) => {
        if (alive && lic && lic.state && lic.state !== 'dormant') setVoiceLicensed(lic.features?.voice === true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [voiceProp]);
  const voiceEnabled = voiceProp && voiceLicensed;
  const location = useLocation();
  // 'collapsed' | 'panel' | 'expanded' | 'rail' (nav-collapsed right edge)
  // | 'stage' (live voice driving the page: bottom strip + ticker)
  const [view, setView] = useState('collapsed');
  const [messages, setMessages] = useState([]);
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceState, setVoiceState] = useState('idle');
  const [muted, setMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  // Voice ↔ copilot bridge: delegate_to_copilot enqueues an agentOS turn on
  // the SAME session the CopilotPanel renders; once delegated, the dock
  // follows that session so the voice can read the plan back and approve.
  const [copilotFollow, setCopilotFollow] = useState(false);
  const copilot = useCopilotTurn({ enabled: copilotFollow });
  // The copilot panel has no launcher of its own: this dock's launcher is the
  // single agent entry. The panel reports { open, busy, navPeek }; the
  // launcher carries that state and a tap re-opens a working or folded
  // copilot instead of toggling the dock.
  const [copilotState, setCopilotState] = useState({ mounted: false, open: false, busy: false, navPeek: false });
  const copilotOpenRef = useRef(false);
  useEffect(() => {
    const onState = (e) => {
      const s = e.detail || {};
      copilotOpenRef.current = !!s.open;
      setCopilotState({ mounted: true, open: !!s.open, busy: !!s.busy, navPeek: !!s.navPeek });
      if (s.open) setView((v) => (v === 'panel' || v === 'expanded' ? 'collapsed' : v));
    };
    window.addEventListener(COPILOT_STATE_EVENT, onState);
    return () => window.removeEventListener(COPILOT_STATE_EVENT, onState);
  }, []);
  const openCopilot = useCallback(() => { setView((v) => (v === 'panel' || v === 'expanded' ? 'collapsed' : v)); requestCopilotOpen(); }, []);
  const copilotRef = useRef(copilot);
  useEffect(() => { copilotRef.current = copilot; }, [copilot]);
  const voiceToldRef = useRef(new Set());

  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const captureCtxRef = useRef(null);
  const playCtxRef = useRef(null);
  const playGainRef = useRef(null);
  const processorRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const barsRef = useRef(null);
  const prevLevelsRef = useRef(new Float32Array(NUM_BARS));
  const sessionReadyRef = useRef(false);
  const micBufferRef = useRef([]);
  const playQueueRef = useRef([]);
  const nextPlayTimeRef = useRef(0);
  const currentSourceRef = useRef(null);
  const activeResponseIdRef = useRef(null);
  const responseHadToolCallsRef = useRef(false);
  const pendingToolPromisesRef = useRef([]); // async tools settle before the follow-up response.create
  const wantResponseRef = useRef(false); // a system note asked to be spoken while a response was active
  const directiveExecRef = useRef(null);     // stable sink for playback-synced directives
  const prevPathRef = useRef(null);          // detect mid-session SPA route changes
  const agentPartialRef = useRef('');
  const abortedRef = useRef(false);
  const voiceStateRef = useRef('idle');
  const sessionStartRef = useRef(0);
  const elapsedTimerRef = useRef(null);
  const scrollRef = useRef(null);
  const mutedRef = useRef(false);
  const speakerMutedRef = useRef(false);
  const volumeRef = useRef(1);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const playQueueStartedRef = useRef(false);
  const reconnectsRef = useRef(0);
  const devNarrationRef = useRef('quiet'); // voice.devNarration from the mint
  const devFollowRef = useRef(null);       // fallback dev session to follow
  const viewRef = useRef('collapsed');
  const railReturnRef = useRef('panel'); // view to restore when leaving the rail
  const voiceActiveRef = useRef(false);
  const stageManualRef = useRef(null); // null = auto · true/false = the person's ⤓ / ⤢ pick
  const lastScreenRef = useRef('');
  const lastScreenAtRef = useRef(0);

  useEffect(() => { viewRef.current = view; }, [view]);
  useEffect(() => { voiceStateRef.current = voiceState; }, [voiceState]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamingText]);

  const setVState = useCallback((s) => {
    voiceStateRef.current = s;
    setVoiceState(s);
  }, []);

  /* ── Stage mode ─────────────────────────────────────────────────
     While a LIVE voice session drives the page (navigate, scroll, zoom,
     click, point) the panel becomes the low bottom band (StageStrip) so
     the screen stays visible. Auto when a driving tool runs; the person's
     ⤓ / ⤢ pick sticks until the session ends. */
  const enterStageAuto = useCallback(() => {
    if (!voiceActiveRef.current || stageManualRef.current === false) return false;
    if (viewRef.current === 'panel' || viewRef.current === 'expanded') railReturnRef.current = viewRef.current;
    if (viewRef.current !== 'stage') setView('stage');
    return true;
  }, []);
  const expandFromStage = useCallback(() => { stageManualRef.current = false; setView(railReturnRef.current || 'panel'); }, []);
  const compactToStage = useCallback(() => {
    stageManualRef.current = true;
    if (viewRef.current === 'panel' || viewRef.current === 'expanded') railReturnRef.current = viewRef.current;
    setView('stage');
  }, []);
  useEffect(() => {
    document.body.classList.toggle('nv-stage-on', view === 'stage');
    return () => { document.body.classList.remove('nv-stage-on'); };
  }, [view]);
  useEffect(() => { voiceActiveRef.current = voiceActive; }, [voiceActive]);
  // Session over → stage mode ends and the manual pick resets to automatic.
  useEffect(() => {
    if (voiceActive) return;
    stageManualRef.current = null;
    if (viewRef.current === 'stage') setView(railReturnRef.current || 'panel');
  }, [voiceActive]);

  /* ── Navigation mode ────────────────────────────────────────────
     When the agent navigates the app, the dock auto-collapses into a
     slim right-edge rail so it doesn't cover the content it just
     navigated to (or the stage strip during a live voice session). The
     session stays hot; clicking the rail restores the previous view. */
  const doNavigate = useCallback((path) => {
    if (!enterStageAuto() && (viewRef.current === 'panel' || viewRef.current === 'expanded')) {
      railReturnRef.current = viewRef.current;
      setView('rail');
    }
    navigate(path);
  }, [navigate, enterStageAuto]);

  // scroll_page has two shapes: the walkthrough glide (target "next" /
  // "prev" / "top" / "bottom" / a section id on this page, no direction) and
  // the mouse wheel (direction / amount / a scrollable area) in pageControls.
  const isWalkScroll = (args) => {
    if (args?.direction || args?.amount) return false;
    const t = String(args?.target || 'next').trim();
    return /^(next|prev|top|bottom)$/.test(t) || (!!safeSection(t) && !!document.getElementById(t));
  };

  /* ── Tool dispatch ─────────────────────────────────────────────
     Client tools run in the browser; everything else goes to the
     backend's allowlisted /voice/tool endpoint.

     With `playbackSync` (live voice sessions), navigation is deferred
     to the audio playhead via the playback clock: the screen changes
     when the narration that triggered it becomes AUDIBLE, not when the
     model generated it (which can lead playback by seconds). The tool
     result returns optimistically — navigate_app carries no data the
     model needs to wait for. */
  // Hand a spoken/typed build request to the agentOS copilot: queue the
  // turn on the shared session (the panel opens and shows the plan card),
  // and start following it so the voice hears plan / result / errors.
  const delegateToCopilot = useCallback(async (request) => {
    const text = String(request || '').trim();
    if (!text) return { error: 'nothing to delegate' };
    try {
      setCopilotFollow(true);
      const id = await copilotRef.current.enqueue(text);
      requestCopilotOpen();
      return { ok: true, messageId: id, note: 'the copilot is planning; a plan card appears in the copilot panel — you will get a COPILOT PLAN note to read back' };
    } catch (err) {
      return { error: err.message };
    }
  }, []);

  // The retrieval trace(s) of the voice turn in progress: /voice/tool answers
  // `trace` beside the result (ask_knowledge's "how I knew" + the think
  // stream). Several tool calls in one turn concatenate their think events;
  // the next committed assistant message carries the result so "How I knew"
  // / Watch work for spoken answers too. [[watch-the-brain-think]]
  const traceRef = useRef(null);
  const [liveThink, setLiveThink] = useState([]); // the voice turn's think events so far (the theatre plays them live)
  const noteTrace = useCallback((trace) => {
    if (!trace) return;
    const prev = traceRef.current;
    const base = trace.hits?.length || !prev ? trace : prev;
    const events = [...(prev?.think?.events || []), ...(trace.think?.events || [])].map((e, i) => ({ ...e, seq: i + 1 }));
    traceRef.current = events.length ? { ...base, think: { ...(trace.think || prev?.think || { v: 1 }), events, total: events.length } } : base;
    if (events.length) setLiveThink(events);
  }, []);
  const takeTrace = () => { const t = traceRef.current; traceRef.current = null; setLiveThink([]); return t; };

  /* ── The think stage ─────────────────────────────────────────────── */
  const [watchThink, setWatchThink] = useState(() => watchPref() === 'on');
  const [brainGraph, setBrainGraph] = useState(null);
  useEffect(() => { if (!watchThink || brainGraph) return undefined; let on = true; loadBrainGraph().then((g) => { if (on) setBrainGraph(g); }); return () => { on = false; }; }, [watchThink, brainGraph]);
  const [thinkUi, setThinkUi] = useState(null);
  useEffect(() => {
    if (view === 'collapsed' || thinkUi) return undefined;
    let on = true;
    fetch(`${API}/voice/ui`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (on) setThinkUi({ ...THINK_UI_DEFAULT, ...(d?.think || {}) }); }).catch(() => { if (on) setThinkUi(THINK_UI_DEFAULT); });
    return () => { on = false; };
  }, [view, thinkUi]);
  const thinkMode = (thinkUi || THINK_UI_DEFAULT).stage; // 'auto' | 'pill' | 'off'
  // Live: the voice turn's events as they arrive. At rest: the last
  // assistant message's trace (a text turn's trace lands whole, and the
  // player replays it).
  const lastTraceThink = useMemo(() => { for (let i = messages.length - 1; i >= 0; i -= 1) { const ev = messages[i]?.trace?.think?.events; if (ev?.length) return ev; } return []; }, [messages]);
  const thinkBusy = liveThink.length > 0;
  const thinkEvents = thinkBusy ? liveThink : lastTraceThink;
  const wideTheatre = useWide(900);
  const thinkAvail = thinkMode !== 'off'; // the stage exists; the header always offers it
  const thinkOn = thinkAvail && thinkEvents.length > 0;
  useEffect(() => {
    if (thinkMode !== 'auto' || !thinkBusy || watchPref() === 'off') return;
    setWatchThink(true);
  }, [thinkMode, thinkBusy, liveThink.length]);
  useEffect(() => {
    if (thinkMode !== 'auto' || thinkBusy || !watchThink || watchPref() === 'on') return undefined;
    const t = setTimeout(() => setWatchThink(false), 4000);
    return () => clearTimeout(t);
  }, [thinkMode, thinkBusy, watchThink]);
  const toggleWatch = useCallback(() => { setWatchThink((v) => { setWatchPref(v ? 'off' : 'on'); return !v; }); }, []);
  // Opened by hand (watchThink) the theatre shows even before the first
  // search, as an empty frame — so turning it on is visible at once.
  const showTheatre = thinkAvail && watchThink && wideTheatre;
  const showInline = thinkAvail && watchThink && !wideTheatre;
  const pulseDots = useMemo(() => {
    const counts = {};
    return thinkEvents.map((e) => { counts[e.type] = (counts[e.type] || 0) + 1; return stageOf(counts).index; }).slice(-40);
  }, [thinkEvents]);
  const thinkPlayer = (height) => (
    <BrainThink graph={brainGraph} events={thinkEvents} live={thinkBusy} height={height} hrefFor={(d) => citationHref({ docId: d })} LinkComponent={Link} empty="Ask something — the search plays here as the agent answers." />
  );

  const executeTool = useCallback(async (name, args, { playbackSync = false } = {}) => {
    if (name === 'navigate_app') {
      // Bare routes ('items') are treated as app-absolute, not dropped.
      const raw = String(args?.path || '/').trim();
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      if (path.length > 1 || raw === '/') {
        if (playbackSync) {
          scheduleDirective('navigate_app', { path }, 'voice-tool');
        } else {
          doNavigate(path);
        }
        return { ok: true, navigatedTo: path };
      }
      return { error: 'invalid path' };
    }
    if (name === 'scroll_page' && isWalkScroll(args)) {
      const target = String(args?.target || 'next').trim();
      enterStageAuto();
      if (playbackSync) scheduleDirective('scroll_page', { target }, 'voice-tool');
      else scrollPage(target);
      return { ok: true, scrolled: target };
    }
    if (isPageControlTool(name)) {
      // Mouse-and-keyboard primitives run now and answer with what happened
      // (utils/pageControls.js refuses marked / irreversible controls).
      enterStageAuto();
      return runPageControl(name, args);
    }
    if (name === 'delegate_to_copilot') return delegateToCopilot(String(args?.request || ''));
    if (name === 'approve_plan') {
      try {
        await copilotRef.current.approve(args?.decision === 'rejected' ? 'rejected' : 'approved');
        return { ok: true, decision: args?.decision, note: args?.decision === 'rejected' ? 'plan rejected' : 'plan approved — the copilot is running it; you will get a COPILOT REPLIED note' };
      } catch (err) {
        return { error: err.message };
      }
    }
    try {
      const res = await fetch(`${API}/voice/tool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ tool: name, params: args, ...reqContext() }),
      });
      const data = await res.json();
      if (!res.ok) return { error: data.error || `tool failed (${res.status})` };
      noteTrace(data.trace);
      return data.result;
    } catch (err) {
      return { error: err.message };
    }
  }, [doNavigate, delegateToCopilot, enterStageAuto, noteTrace]);

  // Voice narration of the copilot's turn: plans waiting for approval, the
  // reply, and failures ride into the realtime session as system items
  // (respond:true) so NoVA reads them back; each message is told once.
  // A response.create while a response is active makes the provider cancel
  // the one speaking (and the note is never read): the COPILOT REPLIED that
  // arrived 5 s after a hand-over was lost and NoVA waited in silence. While
  // a response is active the request is parked and sent from response.done.
  const sendVoiceSystem = useCallback((text, { respond = false } = {}) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !sessionReadyRef.current) return false;
    try {
      ws.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'system', content: [{ type: 'input_text', text }] } }));
      if (respond) {
        if (activeResponseIdRef.current) wantResponseRef.current = true;
        else ws.send(JSON.stringify({ type: 'response.create' }));
      }
      return true;
    } catch (_) { return false; }
  }, []);

  useEffect(() => {
    if (!copilotFollow || !voiceActive) return;
    const msgs = copilot.messages || [];
    for (const m of msgs) {
      if (m.role === 'user' && m.status === 'awaiting_approval' && m.plan && m.uid === copilot.me?.uid) {
        const key = `plan:${m.id}`;
        if (voiceToldRef.current.has(key)) continue;
        voiceToldRef.current.add(key);
        const steps = (m.plan.steps || []).map((st, i) => `${i + 1}. ${st.note || st.tool}`).join('; ');
        sendVoiceSystem(`COPILOT PLAN WAITING — "${m.plan.title || 'Plan'}": ${steps}. Needs a yes.`, { respond: true });
      } else if (m.role === 'user' && m.status === 'error' && m.error) {
        const key = `err:${m.id}`;
        if (voiceToldRef.current.has(key)) continue;
        voiceToldRef.current.add(key);
        sendVoiceSystem(`COPILOT TURN FAILED: ${String(m.error).slice(0, 300)}`, { respond: true });
      } else if (m.role === 'assistant' && m.createdAt > (sessionStartRef.current || 0)) {
        const key = `reply:${m.id}`;
        if (voiceToldRef.current.has(key)) continue;
        voiceToldRef.current.add(key);
        const content = String(m.content || '').trim();
        // A reply that ends in a question is the copilot asking the USER —
        // name it so the voice relays the question instead of "it's planning".
        sendVoiceSystem(`${/\?\s*$/.test(content) ? 'COPILOT ASKS THE USER (relay this question, then wait)' : 'COPILOT REPLIED (on screen already)'}: ${content.slice(0, 1200)}`, { respond: true });
      }
    }
  }, [copilot.messages, copilot.me, copilotFollow, voiceActive, sendVoiceSystem]);

  // Dev-session bridge: the open Dev Console session (or the one the mint
  // named) narrates itself as DEV … items at voice.devNarration's level.
  useDevNarration({ voiceActive, sendVoiceSystem, narrationRef: devNarrationRef, followRef: devFollowRef, sessionStartRef });

  // Directives from the playback clock execute the tool immediately (no
  // re-sync — the clock already deferred them to the playhead).
  useEffect(() => {
    directiveExecRef.current = (name, args) => executeTool(name, args);
  }, [executeTool]);

  /* Commit whatever transcript was already REVEALED (i.e. actually heard)
     when playback is cut short — barge-in, cancel, socket drop. */
  const commitRevealedPartial = useCallback(() => {
    const heard = (agentPartialRef.current || '').trim();
    agentPartialRef.current = '';
    setStreamingText('');
    if (heard) {
      const trace = takeTrace();
      setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: 'assistant', content: heard, ...(trace ? { trace } : {}) }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Playback-clock sync ────────────────────────────────────────────
     Everything user-visible (transcript reveal, navigation directives)
     is scheduled against the AUDIO PLAYHEAD, not frame arrival.
     lagMs = scheduled-end − currentTime at any arrival instant is
     exactly how far ahead that content is. */
  useEffect(() => {
    configurePlaybackClock({
      getLagMs: () => {
        const ctx = playCtxRef.current;
        if (!ctx) return 0;
        return Math.max(0, (nextPlayTimeRef.current - ctx.currentTime) * 1000);
      },
      onTranscript: (text) => {
        agentPartialRef.current += text;
        setStreamingText(agentPartialRef.current);
      },
      onTranscriptCommit: (fullText) => {
        const text = (fullText || agentPartialRef.current || '').trim();
        agentPartialRef.current = '';
        setStreamingText('');
        if (text) {
          const trace = takeTrace();
          setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: 'assistant', content: text, ...(trace ? { trace } : {}) }]);
        }
      },
      onDirective: (name, args) => { try { directiveExecRef.current?.(name, args); } catch (_) {} },
    });
    return () => stopPlaybackClock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Mid-session route-change sync ──────────────────────────────────
     SPA navigation (agent-driven or user-driven) doesn't unmount this
     component — the voice WS stays open while the page changes under
     it. Tell the model what's now on screen so follow-up questions are
     answered from the current page, not the one the session started on. */
  // A fresh outline of the screen (handles like [b3] the page controls act
  // on) rides into the live session as a system item: on route change, when
  // the session opens, when the person starts speaking (≥4 s apart) and
  // 600 ms after a driving tool. Identical outlines are not resent.
  const sendScreen = useCallback((reason) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !sessionReadyRef.current) return false;
    const outline = captureScreenOutline({ maxChars: VOICE_SCREEN_CHARS });
    if (!outline) return false;
    if (reason === 'speech') {
      if (Date.now() - (lastScreenAtRef.current || 0) < SCREEN_MIN_GAP_MS) return false;
    }
    if (outline === lastScreenRef.current && reason !== 'route') return false;
    lastScreenRef.current = outline;
    lastScreenAtRef.current = Date.now();
    try {
      const path = window.location.pathname;
      const text = reason === 'route'
        ? `PAGE CHANGED (SPA navigation): the user is now viewing ${path}. Ground follow-up answers in this page.\n${screenContextItem(outline, '', path)}`
        : screenContextItem(outline, '', path);
      ws.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'system', content: [{ type: 'input_text', text }] } }));
      return true;
    } catch (_) { return false; }
  }, []);
  const sendScreenRef = useRef(sendScreen);
  useEffect(() => { sendScreenRef.current = sendScreen; }, [sendScreen]);

  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (prev === null || prev === location.pathname) return;
    // A beat later so the new route has rendered before the outline is read.
    const t = setTimeout(() => sendScreenRef.current('route'), 400);
    return () => clearTimeout(t);
  }, [location.pathname]);

  /* ── Audio output (scheduled PCM queue + gain for speaker mute/volume) ── */
  const applyOutputGain = useCallback(() => {
    if (playGainRef.current) {
      playGainRef.current.gain.value = speakerMutedRef.current ? 0 : volumeRef.current;
    }
  }, []);

  const stopPlayback = useCallback(() => {
    playQueueRef.current = [];
    if (playGainRef.current && playCtxRef.current) {
      try { playGainRef.current.disconnect(); } catch (_) {}
      const gain = playCtxRef.current.createGain();
      gain.connect(playCtxRef.current.destination);
      playGainRef.current = gain;
      applyOutputGain();
    }
    if (currentSourceRef.current) {
      try { currentSourceRef.current.stop(); } catch (_) {}
      currentSourceRef.current = null;
    }
    nextPlayTimeRef.current = 0;
  }, [applyOutputGain]);

  const drainPlayQueue = useCallback(() => {
    if (!playCtxRef.current || playQueueRef.current.length === 0) return;
    const ctx = playCtxRef.current;
    playQueueStartedRef.current = true;
    if (ctx.state !== 'running') {
      // Autoplay policy still holds the output: ask for a tap instead of
      // playing into silence. The queue is kept and drained on resume.
      ctx.resume().then(() => setSoundBlocked(ctx.state !== 'running')).catch(() => setSoundBlocked(true));
      if (ctx.state !== 'running') { setSoundBlocked(true); return; }
    }
    const dest = playGainRef.current || ctx.destination;
    while (playQueueRef.current.length > 0) {
      const chunk = playQueueRef.current.shift();
      const buffer = ctx.createBuffer(1, chunk.length, SAMPLE_RATE);
      buffer.getChannelData(0).set(chunk);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(dest);
      const startAt = Math.max(ctx.currentTime, nextPlayTimeRef.current);
      source.start(startAt);
      nextPlayTimeRef.current = startAt + buffer.duration;
      currentSourceRef.current = source;
    }
  }, []);

  // Runs inside a tap, so resume() is allowed to succeed where the
  // programmatic one was not; then flush whatever queued while blocked.
  const unlockSound = useCallback(() => {
    const ctx = playCtxRef.current;
    if (!ctx) { setSoundBlocked(false); return; }
    ctx.resume().then(() => { setSoundBlocked(ctx.state !== 'running'); drainPlayQueue(); }).catch(() => {});
  }, [drainPlayQueue]);

  const playBase64Audio = useCallback((base64Data) => {
    try {
      const binStr = atob(base64Data);
      const bytes = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
      const pcm16 = new Int16Array(bytes.buffer);
      const f32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) f32[i] = pcm16[i] / 32768.0;
      playQueueRef.current.push(f32);
      // Feed the playback clock's speech-rate estimate (transcript pacing).
      noteAudioScheduled((f32.length / SAMPLE_RATE) * 1000);
      drainPlayQueue();
    } catch (err) {
      console.warn('[VoiceAgent] audio decode error:', err.message);
    }
  }, [drainPlayQueue]);

  /* ── Mic capture ───────────────────────────────────────────────── */
  const handleMicChunk = useCallback((f32) => {
    if (mutedRef.current) return; // muted: send nothing, not silence
    if (!sessionReadyRef.current) {
      if (micBufferRef.current.length < MAX_MIC_BUFFER) micBufferRef.current.push(f32);
      return;
    }
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: float32ToBase64PCM16(f32) }));
  }, []);

  const attachMic = useCallback(async (ctx, micStream) => {
    const source = ctx.createMediaStreamSource(micStream);
    let node;
    try {
      const url = URL.createObjectURL(new Blob([MIC_WORKLET_SRC], { type: 'application/javascript' }));
      try { await ctx.audioWorklet.addModule(url); } finally { URL.revokeObjectURL(url); }
      node = new AudioWorkletNode(ctx, 'va-mic-capture', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      node.port.onmessage = (e) => handleMicChunk(e.data);
    } catch (err) {
      console.warn('[VoiceAgent] AudioWorklet unavailable, using ScriptProcessor:', err.message);
      node = ctx.createScriptProcessor(MIC_CHUNK, 1, 1);
      node.onaudioprocess = (e) => handleMicChunk(new Float32Array(e.inputBuffer.getChannelData(0)));
    }
    source.connect(node);
    node.connect(ctx.destination); // keeps the node pulled; output is silent
    processorRef.current = node;
  }, [handleMicChunk]);

  /* ── Visualiser ────────────────────────────────────────────────── */
  const startVisualiser = useCallback((ctx, micStream) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { analyserRef.current && analyserRef.current.disconnect(); } catch (_) {}
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.3;
    ctx.createMediaStreamSource(micStream).connect(analyser);
    analyserRef.current = analyser;
    const bins = analyser.frequencyBinCount;
    const data = new Uint8Array(bins);
    const prev = prevLevelsRef.current;
    let t = 0;
    const tick = () => {
      analyser.getByteFrequencyData(data);
      const isSpeaking = voiceStateRef.current === 'speaking';
      t += 0.08;
      const step = Math.max(1, Math.floor(bins / NUM_BARS));
      const bars = barsRef.current?.children;
      for (let i = 0; i < NUM_BARS; i++) {
        const idx = Math.min(i * step, bins - 1);
        let raw = data[idx] / 255;
        if (isSpeaking) {
          const wave = 0.3 + 0.25 * Math.sin(t + i * 0.35) + 0.15 * Math.sin(t * 1.7 + i * 0.2) + 0.1 * Math.random();
          raw = Math.max(raw, wave);
        }
        const smooth = raw > prev[i] ? ATTACK : DECAY;
        const val = prev[i] * smooth + raw * (1 - smooth);
        prev[i] = val;
        if (bars && bars[i]) bars[i].style.height = `${Math.max(2, val * 20)}px`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const stopVisualiser = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { analyserRef.current && analyserRef.current.disconnect(); } catch (_) {}
    analyserRef.current = null;
    prevLevelsRef.current = new Float32Array(NUM_BARS);
    const bars = barsRef.current?.children;
    if (bars) for (let i = 0; i < bars.length; i++) bars[i].style.height = '2px';
  }, []);

  /* ── Session lifecycle ─────────────────────────────────────────── */
  const stopVoice = useCallback(() => {
    abortedRef.current = true;
    stopVisualiser();
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    if (processorRef.current) { try { processorRef.current.disconnect(); } catch (_) {} processorRef.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch (_) {} wsRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (captureCtxRef.current) { captureCtxRef.current.close().catch(() => {}); captureCtxRef.current = null; }
    if (playGainRef.current) { try { playGainRef.current.disconnect(); } catch (_) {} playGainRef.current = null; }
    if (playCtxRef.current) { playCtxRef.current.close().catch(() => {}); playCtxRef.current = null; }
    sessionReadyRef.current = false;
    activeResponseIdRef.current = null;
    wantResponseRef.current = false;
    micBufferRef.current = [];
    playQueueRef.current = [];
    nextPlayTimeRef.current = 0;
    reconnectsRef.current = 0;
    playQueueStartedRef.current = false;
    setSoundBlocked(false);
    clearPlaybackQueues();
    pendingToolPromisesRef.current = [];
    setVoiceActive(false);
    setVState('idle');
    setMuted(false);
    mutedRef.current = false;
    setStreamingText('');
    setElapsed(0);
    sessionStartRef.current = 0;
    agentPartialRef.current = '';
  }, [stopVisualiser, setVState]);

  // Where the user is, on every backend call: route + surface ('dev' inside
  // the Dev Console) + a short outline of what is rendered (screenOutline.js)
  // for the session mint and text chat. Facts for the prompt; the permission
  // boundary is the registry (internal claim resolved server-side).
  const reqContext = ({ screen = false } = {}) => ({
    page: `${window.location.pathname}${window.location.search}`.slice(0, 300),
    surface: window.location.pathname.startsWith('/dev') ? 'dev' : 'instance',
    ...(screen ? { screen: captureScreenOutline({ maxChars: VOICE_SCREEN_CHARS }) } : {}),
  });

  const fetchSession = useCallback(async () => {
    const r = await fetch(`${API}/voice/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      // devSession: the console's open session, so the mint's DEV CONTEXT is
      // about it (else the backend picks the user's most recent one).
      body: JSON.stringify({ ...reqContext({ screen: true }), ...(getActiveDevSession() ? { devSession: getActiveDevSession() } : {}) }),
    });
    if (!r.ok) {
      let detail = '';
      try { detail = (await r.json()).error || ''; } catch (_) {}
      throw new Error(detail || `session failed (${r.status})`);
    }
    return r.json();
  }, []);

  /* Opens the realtime WebSocket for a fetched session. Named so the
     reconnect path inside onclose can call it again with a fresh token. */
  const connectWs = useCallback(function connect(sessionData) {
    const { ephemeralToken, wsUrl, wsProtocols, sessionConfig, greet, dev } = sessionData;
    if (!ephemeralToken) throw new Error('no ephemeral token from /voice/session');
    sessionReadyRef.current = false;
    // Spoken once per connect (not on token-refresh reconnects).
    const greetOnce = greet && reconnectsRef.current === 0 ? greet : '';
    // Dev bridge: narration level + the session to follow when the console
    // has none open; after a reconnect the fresh socket gets DEV CONTEXT.
    devNarrationRef.current = dev?.narration || 'quiet';
    devFollowRef.current = dev?.follow || null;
    const devReseed = reconnectsRef.current > 0 && dev?.contextLine ? dev.contextLine : '';
    const ws = new WebSocket(wsUrl, wsProtocols);
    wsRef.current = ws;
    ws.onopen = () => {
      try { ws.send(JSON.stringify({ type: 'session.update', session: sessionConfig })); } catch (_) {}
    };
    ws.onmessage = async (e) => {
      let evt;
      try { evt = JSON.parse(e.data); } catch { return; }
      switch (evt.type) {
        case 'session.updated':
          sessionReadyRef.current = true;
          reconnectsRef.current = 0;
          setError(null);
          setVState('listening');
          setTimeout(() => sendScreenRef.current('session'), 300);
          if (micBufferRef.current.length > 0 && ws.readyState === WebSocket.OPEN) {
            micBufferRef.current.forEach((chunk) => {
              ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: float32ToBase64PCM16(chunk) }));
            });
            micBufferRef.current = [];
          }
          // The opener (/voice/session `greet`): the first-build offer on a
          // fresh instance, else one hello — no dead air after "Start voice".
          if (greetOnce && !activeResponseIdRef.current && ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'system', content: [{ type: 'input_text', text: `SESSION OPENED. ${greetOnce} One or two sentences, then listen.` }] } }));
              ws.send(JSON.stringify({ type: 'response.create' }));
            } catch (_) {}
          }
          // Reconnected mid-session: the new socket learns where the dev
          // session stands (not spoken — the person already knows).
          if (devReseed && ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'system', content: [{ type: 'input_text', text: `${devReseed} (Continuity note after a reconnect — do not read this out; answer "where were we" from it.)` }] } })); } catch (_) {}
          }
          break;
        case 'error':
          setError((evt.error && (evt.error.message || evt.error.code)) || 'Voice service error');
          break;
        case 'input_audio_buffer.speech_started':
          // Barge-in: stop playback and cancel the in-flight response.
          // Drop everything scheduled-but-unspoken; keep what was heard.
          stopPlayback();
          clearPlaybackQueues();
          commitRevealedPartial();
          setVState('listening');
          sendScreenRef.current('speech');
          if (activeResponseIdRef.current && ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: 'response.cancel' })); } catch (_) {}
          }
          activeResponseIdRef.current = null;
          break;
        case 'input_audio_buffer.speech_stopped':
          setVState('processing');
          break;
        case 'response.created':
          activeResponseIdRef.current = evt.response?.id || true;
          responseHadToolCallsRef.current = false;
          // Straggler guard: a previous response that never got its
          // transcript.done leaves revealed text behind — commit it so
          // the new response doesn't append onto it.
          if (agentPartialRef.current) commitRevealedPartial();
          break;
        case 'response.output_audio.delta':
          if (!activeResponseIdRef.current) break;
          setVState('speaking');
          if (evt.delta) playBase64Audio(evt.delta);
          break;
        case 'response.output_audio_transcript.delta':
          if (!activeResponseIdRef.current) break;
          // Playback-synced reveal: the fragment becomes visible when the
          // audio it belongs to is audible, not when the frame arrives.
          scheduleTranscript(evt.delta || '');
          break;
        case 'response.output_audio_transcript.done':
          // Queue the commit BEHIND the pending reveals so the message
          // finalizes exactly when its last word is spoken.
          scheduleTranscriptCommit((evt.transcript || '').trim());
          break;
        case 'response.done': {
          activeResponseIdRef.current = null;
          // NOTE: do NOT clear agentPartialRef/streamingText here — the
          // playback-synced reveal queue is still speaking; the commit
          // scheduled by transcript.done finalizes the message in-order.
          if ((responseHadToolCallsRef.current || wantResponseRef.current) && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            // Tool outputs were appended (or a system note is waiting to be
            // spoken) — ask for a follow-up response once the audio queue
            // drains AND every async tool result has landed in the
            // conversation (the function_call_output must be there before
            // the model speaks again).
            responseHadToolCallsRef.current = false;
            wantResponseRef.current = false;
            const ctx = playCtxRef.current;
            const drainMs = ctx ? Math.max(0, (nextPlayTimeRef.current - ctx.currentTime) * 1000) + 50 : 50;
            setVState('thinking');
            const pendingTools = pendingToolPromisesRef.current.splice(0);
            setTimeout(() => {
              Promise.allSettled(pendingTools).then(() => {
                try {
                  if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    wsRef.current.send(JSON.stringify({ type: 'response.create' }));
                  }
                } catch (_) {}
              });
            }, drainMs);
          } else {
            // Flip to "listening" when the audio finishes PLAYING, not when
            // generation finishes — the UI stays in speaking state for as
            // long as the voice is actually audible.
            watchDrain(() => {
              if (voiceStateRef.current === 'speaking' || voiceStateRef.current === 'processing') {
                setVState('listening');
              }
            });
          }
          break;
        }
        case 'response.cancelled':
          stopPlayback();
          clearPlaybackQueues();
          commitRevealedPartial();
          activeResponseIdRef.current = null;
          if (wantResponseRef.current && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wantResponseRef.current = false;
            try { wsRef.current.send(JSON.stringify({ type: 'response.create' })); } catch (_) {}
          }
          break;
        case 'conversation.item.input_audio_transcription.completed': {
          const userText = (evt.transcript || '').trim();
          if (userText) {
            setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content: userText }]);
          }
          break;
        }
        case 'response.function_call_arguments.done': {
          const fnName = evt.name;
          const callId = evt.call_id;
          let argsObj = {};
          try { argsObj = evt.arguments ? JSON.parse(evt.arguments) : {}; } catch (_) {}
          responseHadToolCallsRef.current = true;
          // Tools settle asynchronously (backend fetches); send the output
          // when the tool settles and register the promise so the follow-up
          // response.create in response.done waits for it — otherwise the
          // model could speak before the result is in the conversation.
          const toolP = Promise.resolve()
            .then(() => executeTool(fnName, argsObj, { playbackSync: true }))
            .catch((err) => ({ error: String(err?.message || err) }))
            .then((result) => {
              try {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && callId) {
                  wsRef.current.send(JSON.stringify({
                    type: 'conversation.item.create',
                    item: {
                      type: 'function_call_output',
                      call_id: callId,
                      output: JSON.stringify(result || { ok: true }),
                    },
                  }));
                  if (DRIVING_TOOLS.has(fnName)) setTimeout(() => sendScreenRef.current('after-tool'), 600);
                }
              } catch (_) {}
            });
          pendingToolPromisesRef.current.push(toolP);
          break;
        }
        default:
          break;
      }
    };
    ws.onerror = () => {}; // onclose carries the recovery logic
    ws.onclose = (evt) => {
      if (abortedRef.current || wsRef.current !== ws) return;
      wsRef.current = null;
      sessionReadyRef.current = false;
      activeResponseIdRef.current = null;
      stopPlayback();
      clearPlaybackQueues();
      commitRevealedPartial(); // keep what was actually heard
      // Unexpected drop with the mic still live: reconnect with a fresh
      // ephemeral token (the old one is single-use and ~5 min anyway).
      if (streamRef.current && reconnectsRef.current < maxReconnects()) {
        reconnectsRef.current += 1;
        setVState('reconnecting');
        setTimeout(async () => {
          if (abortedRef.current || !streamRef.current) return;
          try {
            const data = await fetchSession();
            if (abortedRef.current || !streamRef.current) return;
            connect(data);
          } catch (err) {
            setError(`Voice reconnect failed: ${err.message}`);
            stopVoice();
          }
        }, 500 * Math.min(reconnectsRef.current, 4));
      } else {
        if (evt && evt.code !== 1000) setError(`Voice ended (${evt.code}${evt.reason ? `: ${evt.reason}` : ''})`);
        stopVoice();
      }
    };
  }, [commitRevealedPartial, executeTool, fetchSession, playBase64Audio, setVState, stopPlayback, stopVoice]);

  const startVoice = useCallback(async () => {
    if (voiceActive) return;
    abortedRef.current = false;
    sessionReadyRef.current = false;
    micBufferRef.current = [];
    playQueueRef.current = [];
    nextPlayTimeRef.current = 0;
    reconnectsRef.current = 0;
    playQueueStartedRef.current = false;
    setSoundBlocked(false);
    setError(null);
    setVState('connecting');
    setVoiceActive(true);
    try {
      // Unlock playback inside the tap that started voice. Creating the
      // context after getUserMedia returns leaves it suspended on Android
      // Chrome, so the speaker looks on and stays silent (same fix as the
      // console's useVoiceSession).
      // Playback runs at the device's native rate: forcing 24 kHz on the
      // output context is silent on some Android (Samsung) devices once the
      // mic puts the phone in communication mode. Buffers are tagged
      // SAMPLE_RATE in drainPlayQueue and the browser resamples.
      const AC = window.AudioContext || window.webkitAudioContext;
      const playCtx = new AC();
      playCtxRef.current = playCtx;
      const gain = playCtx.createGain();
      gain.connect(playCtx.destination);
      playGainRef.current = gain;
      applyOutputGain();
      playCtx.onstatechange = () => setSoundBlocked(playCtx.state !== 'running' && playQueueStartedRef.current);
      playCtx.resume().catch(() => {});

      const [micStream, sessionData] = await Promise.all([
        navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        }),
        fetchSession(),
      ]);
      if (abortedRef.current) { micStream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = micStream;

      const captureCtx = new AC({ sampleRate: SAMPLE_RATE });
      captureCtxRef.current = captureCtx;

      await attachMic(captureCtx, micStream);
      if (abortedRef.current) return;

      startVisualiser(captureCtx, micStream);
      sessionStartRef.current = Date.now();
      setElapsed(0);
      elapsedTimerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - sessionStartRef.current) / 1000));
      }, 1000);

      connectWs(sessionData);
    } catch (err) {
      console.error('[VoiceAgent] start failed:', err);
      setError(err && err.name === 'NotAllowedError' ? 'Microphone permission denied.' : err.message || 'Failed to start voice');
      stopVoice();
    }
  }, [voiceActive, applyOutputGain, attachMic, connectWs, fetchSession, setVState, startVisualiser, stopVoice]);

  /* ── Audio controls ────────────────────────────────────────────── */
  const toggleMute = useCallback(() => {
    if (!streamRef.current) return;
    const track = streamRef.current.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    mutedRef.current = !track.enabled;
    setMuted(!track.enabled);
  }, []);

  const toggleSpeaker = useCallback(() => {
    speakerMutedRef.current = !speakerMutedRef.current;
    setSpeakerMuted(speakerMutedRef.current);
    applyOutputGain();
  }, [applyOutputGain]);

  const onVolumeChange = useCallback((e) => {
    const v = Number(e.target.value);
    volumeRef.current = v;
    setVolume(v);
    applyOutputGain();
  }, [applyOutputGain]);

  /* ── Text chat ─────────────────────────────────────────────────── */
  const sendText = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    setChatInput('');
    setError(null);
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content: text }]);

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && sessionReadyRef.current) {
      // Live voice session: inject the text as a user turn; the agent
      // answers aloud through the normal realtime flow.
      if (activeResponseIdRef.current) {
        try { ws.send(JSON.stringify({ type: 'response.cancel' })); } catch (_) {}
        stopPlayback();
        clearPlaybackQueues();
        commitRevealedPartial();
        activeResponseIdRef.current = null;
      }
      try {
        ws.send(JSON.stringify({
          type: 'conversation.item.create',
          item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
        }));
        ws.send(JSON.stringify({ type: 'response.create' }));
      } catch (_) {}
      return;
    }

    // No session: text-chat fallback via the backend (same tool registry).
    setChatBusy(true);
    try {
      const history = [...messages, { role: 'user', content: text }]
        .slice(-16)
        .map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API}/voice/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ messages: history, ...reqContext({ screen: true }) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `chat failed (${res.status})`);
      (data.actions || []).forEach((a) => {
        const raw = String(a?.params?.path || '').trim();
        if (a.tool === 'navigate_app' && raw) doNavigate(raw.startsWith('/') ? raw : `/${raw}`);
        if (a.tool === 'scroll_page' && isWalkScroll(a.params)) scrollPage(String(a?.params?.target || 'next'));
        else if (isPageControlTool(a.tool)) runPageControl(a.tool, a.params || {}).catch(() => {});
        if (a.tool === 'delegate_to_copilot' && a?.params?.request) {
          delegateToCopilot(String(a.params.request)).then((r) => {
            if (r?.error) setError(r.error);
          });
        }
      });
      if (data.reply) {
        setMessages((prev) => [...prev, {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: data.reply,
          citations: Array.isArray(data.citations) ? data.citations : [],
          ...(data.trace ? { trace: data.trace } : {}),
        }]);
      }
    } catch (err) {
      setError(err.message || 'Chat failed');
    } finally {
      setChatBusy(false);
    }
  }, [chatInput, chatBusy, messages, doNavigate, stopPlayback, commitRevealedPartial, delegateToCopilot]);

  /* ── Global shortcuts (capture phase so app-level handlers can't eat
     them): Cmd/Ctrl+K toggle · Esc collapse · Cmd/Ctrl+Shift+V voice ·
     Cmd/Ctrl+Shift+M mute mic. */
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      const key = (e.key || '').toLowerCase();
      if (mod && !e.shiftKey && key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        if (viewRef.current === 'collapsed') requestCopilotClose();
        setView((v) => (v === 'collapsed' ? 'panel' : v === 'rail' ? railReturnRef.current : 'collapsed'));
      } else if (key === 'escape' && copilotOpenRef.current) {
        requestCopilotClose();
      } else if (key === 'escape' && viewRef.current !== 'collapsed') {
        setView('collapsed');
      } else if (mod && e.shiftKey && key === 'v' && voiceEnabled) {
        e.preventDefault();
        e.stopPropagation();
        if (voiceActive) stopVoice(); else { setView((v) => (v === 'collapsed' ? 'panel' : v)); startVoice(); }
      } else if (mod && e.shiftKey && key === 'm') {
        e.preventDefault();
        e.stopPropagation();
        toggleMute();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [voiceEnabled, voiceActive, startVoice, stopVoice, toggleMute]);

  useEffect(() => () => stopVoice(), [stopVoice]);

  if (!API) return null;

  const stateLabel =
    voiceState === 'listening' ? 'Listening' :
    voiceState === 'speaking' ? 'Speaking' :
    voiceState === 'processing' || voiceState === 'thinking' ? 'Thinking' :
    voiceState === 'connecting' ? 'Connecting' :
    voiceState === 'reconnecting' ? 'Reconnecting' : 'Idle';

  // The STATUS (dot + label + hint + clock) says what the agent is doing
  // and what you can do; the ACTION button names the thing it does — "End
  // voice", "Cancel" while connecting — never the state, which read as a
  // label and left people unsure how to stop.
  const stateHint =
    voiceState === 'listening' ? 'go ahead' :
    voiceState === 'speaking' ? 'talk to interrupt' :
    voiceState === 'connecting' || voiceState === 'reconnecting' ? 'a moment' : '';
  const endLabel = voiceState === 'connecting' ? 'Cancel' : '■ End voice';

  const mmss = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;

  const renderMessage = (m) => (
    <div key={m.id} className={`va-msg va-msg-${m.role}`}>
      {m.role === 'assistant' ? (
        <div className="va-md">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
        </div>
      ) : (
        m.content
      )}
      {m.citations && m.citations.length > 0 && (
        <div className="va-citations">
          {m.citations.map((c) => {
            const href = citationHref(c);
            return href
              ? <Link key={c.ref} to={href} className={`va-cite is-link is-${citationKind(c)}`} title={`${c.docId} — open on the Brain page`}>[{c.ref}] {c.title}</Link>
              : <span key={c.ref} className="va-cite" title={c.docId}>[{c.ref}] {c.title}</span>;
          })}
        </div>
      )}
      {m.trace && <HowIKnew trace={m.trace} LinkComponent={Link} hrefFor={(h) => citationHref(h)} loadGraph={loadBrainGraph} thinkHref={THINK_HREF} />}
    </div>
  );

  if (view === 'stage') {
    return (
      <StageStrip
        name="Agent"
        live={voiceActive}
        voiceState={voiceState}
        stateLabel={stateLabel}
        clock={mmss}
        muted={muted}
        onToggleMute={toggleMute}
        onEnd={stopVoice}
        onExpand={expandFromStage}
        barsRef={barsRef}
        messages={messages}
        streamingText={streamingText}
        think={thinkAvail && thinkEvents.length ? { events: thinkEvents, live: thinkBusy } : null}
        onThinkExpand={() => { expandFromStage(); setWatchThink(true); }}
      />
    );
  }

  /* Nav-collapsed rail: slim right-edge strip, visually distinct from the
     idle launcher (accent border, live state), session stays hot. */
  if (view === 'rail') {
    return (
      <div className="va-rail" role="dialog" aria-label="Agent (collapsed for navigation)">
        <button
          type="button"
          className="va-rail-body"
          onClick={() => setView(railReturnRef.current)}
          title="Re-open the agent"
        >
          <span className={`va-launcher-orb ${voiceActive ? 'va-rail-orb-live' : ''}`} aria-hidden />
          <span className="va-rail-label">{voiceActive ? stateLabel : 'Agent'}</span>
          {voiceActive && <span className="va-rail-clock">{mmss}</span>}
        </button>
        {voiceActive && (
          <button
            type="button"
            className={`va-rail-mute ${muted ? 'va-mute-on' : ''}`}
            onClick={toggleMute}
            aria-pressed={muted}
            title={muted ? 'Unmute microphone' : 'Mute microphone'}
          >
            {muted ? 'Unmute' : 'Mute'}
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`va-launcher ${voiceActive ? 'va-launcher-live' : ''} ${voiceActive && muted ? 'va-launcher-muted' : ''} ${!voiceActive && copilotState.busy ? 'va-launcher-copilot-busy' : ''} ${!voiceActive && !copilotState.busy && copilotState.navPeek ? 'va-launcher-copilot-peek' : ''}`}
        onClick={() => {
          if (copilotState.open) { requestCopilotClose(); return; }
          if (!voiceActive && (copilotState.busy || copilotState.navPeek)) { openCopilot(); return; }
          if (view === 'collapsed') requestCopilotClose();
          setView((v) => (v === 'collapsed' ? 'panel' : 'collapsed'));
        }}
        title={
          copilotState.open ? 'Close the copilot'
            : !voiceActive && copilotState.navPeek ? 'Copilot folded for navigation — tap to re-open'
              : view === 'collapsed' ? `Open the agent (${MOD}K)` : `Close the agent (${MOD}K)`
        }
      >
        <span className="va-launcher-orb" aria-hidden />
        <span className="va-launcher-label">
          {voiceActive ? (muted ? `${stateLabel} · Mic muted` : stateLabel)
            : copilotState.open ? 'Close'
              : copilotState.busy ? 'Copilot working'
                : copilotState.navPeek ? 'Copilot' : 'Agent'}
        </span>
      </button>

      {view !== 'collapsed' && (
        <div
          className={`va-panel ${view === 'expanded' ? 'va-panel-expanded' : ''} ${showTheatre ? 'va-panel-theatre' : ''}`}
          role="dialog"
          aria-label="Agent"
        >
          <header className="va-head">
            <span className="va-head-title">Agent</span>
            {thinkOn && pulseDots.length > 0 ? (
              <button type="button" className={`va-pulse ${thinkBusy ? 'is-live' : ''}`} onClick={toggleWatch} aria-pressed={watchThink} title={watchThink ? 'Hide how the agent is thinking' : 'Watch the brain think'} data-nova-ignore="true">
                {pulseDots.map((s, i) => <i key={i} className={`is-s${s}`} />)}
              </button>
            ) : thinkAvail && (
              <button type="button" className={`va-head-btn va-watch-head ${watchThink ? 'is-on' : ''}`} onClick={toggleWatch} aria-pressed={watchThink} title={watchThink ? 'Hide the brain-search view' : 'Show how the agent searches the brain as it answers'} data-nova-ignore="true">
                {watchThink ? 'Watching' : 'Watch'}
              </button>
            )}
            <span className="va-head-actions">
              {copilotState.mounted && (
                <button
                  type="button"
                  className="va-head-btn"
                  onClick={openCopilot}
                  title="Open the agentOS copilot — build, run and change things with a plan you approve"
                >
                  Copilot
                </button>
              )}
              <button
                type="button"
                className="va-head-btn"
                onClick={() => setMessages([])}
                disabled={messages.length === 0 && !streamingText}
                title="New chat (clears the transcript)"
              >
                New chat
              </button>
              {voiceActive && (
                <button
                  type="button"
                  className="va-head-btn"
                  onClick={compactToStage}
                  title="Stage mode — shrink to a strip at the bottom so the page stays visible"
                  aria-label="Shrink the agent to the stage strip"
                >
                  ⤓
                </button>
              )}
              <button
                type="button"
                className="va-head-btn"
                onClick={() => setView((v) => (v === 'expanded' ? 'panel' : 'expanded'))}
                title={view === 'expanded' ? 'Shrink' : 'Expand'}
                aria-label={view === 'expanded' ? 'Shrink the agent panel' : 'Expand the agent panel'}
              >
                {view === 'expanded' ? '⤡' : '⤢'}
              </button>
              <button type="button" className="va-head-close" onClick={() => setView('collapsed')} aria-label="Close">×</button>
            </span>
          </header>

          <div className="va-cols">
          <div className="va-body">
          {voiceEnabled && (
            <button
              type="button"
              className={`va-cta ${voiceActive ? 'va-cta-live' : ''}`}
              onClick={voiceActive ? stopVoice : startVoice}
              title={voiceActive ? 'End the voice session — the transcript stays' : 'Talk to the agent'}
            >
              {voiceActive ? endLabel : '🎙 Start voice'}
            </button>
          )}

          {voiceActive && (
            <>
              <div className={`va-live va-live-${voiceState}`} role="status" aria-live="polite">
                <span className="va-state">
                  <i className="va-state-dot" aria-hidden="true" />
                  <b>{stateLabel}</b>{stateHint ? <span className="va-state-hint"> — {stateHint}</span> : null}
                  <span className="va-state-clock"> · {mmss}</span>
                </span>
                <div ref={barsRef} className="va-bars">
                  {Array.from({ length: NUM_BARS }).map((_, i) => <span key={i} className="va-bar" />)}
                </div>
                <button
                  type="button"
                  className={`va-mute ${muted ? 'va-mute-on' : ''}`}
                  onClick={toggleMute}
                  aria-pressed={muted}
                  title={muted ? `Unmute the microphone (${MOD}⇧M)` : `Mute the microphone — the agent keeps talking (${MOD}⇧M)`}
                >
                  {muted ? '🔇 Unmute mic' : '🎙 Mute mic'}
                </button>
              </div>
              <div className="va-audio">
                <button
                  type="button"
                  className={`va-mute ${speakerMuted ? 'va-mute-on' : ''}`}
                  onClick={toggleSpeaker}
                  aria-pressed={speakerMuted}
                  title={speakerMuted ? 'Unmute the assistant voice' : 'Mute the assistant voice'}
                >
                  {speakerMuted ? 'Speaker off' : 'Speaker on'}
                </button>
                <input
                  type="range"
                  className="va-vol"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={onVolumeChange}
                  disabled={speakerMuted}
                  aria-label="Assistant volume"
                />
              </div>
            </>
          )}
          {error && <div className="va-error">{error}</div>}
          {soundBlocked && (
            <button type="button" className="va-sound-unlock" onClick={unlockSound}>
              Tap for sound — the browser is holding the speaker until you touch the page
            </button>
          )}

          <div ref={scrollRef} className="va-conv">
            {messages.length === 0 && !streamingText && (
              <p className="va-empty">
                {voiceEnabled
                  ? 'Ask anything — answers come from the app\u2019s live knowledge corpus with citations, and the agent can navigate the app while it talks. Talk, or type below.'
                  : 'Ask anything — answers come from the app\u2019s live knowledge corpus with citations.'}
              </p>
            )}
            {messages.map(renderMessage)}
            {thinkBusy && (
              <div className="va-activity" role="status" aria-live="polite">
                <span className="va-activity-dot" aria-hidden="true" />
                <span>Searching</span>
                {thinkOn && (
                  <button type="button" className={`va-watch ${watchThink ? 'is-on' : ''}`} onClick={toggleWatch} aria-pressed={watchThink} title="Watch the brain think">{watchThink ? 'hide' : 'watch'}</button>
                )}
              </div>
            )}
            {showInline && (
              <div className="va-think" data-nova-ignore="true">
                {thinkPlayer(220)}
              </div>
            )}
            {streamingText && (
              <div className="va-msg va-msg-assistant va-msg-streaming">{streamingText}▍</div>
            )}
            {chatBusy && <div className="va-msg va-msg-assistant va-msg-streaming">…</div>}
          </div>

          <form
            className="va-chat"
            onSubmit={(e) => { e.preventDefault(); sendText(); }}
          >
            <input
              className="va-chat-input"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder={voiceActive ? 'Type instead of talking…' : 'Type a message…'}
              aria-label="Message the agent"
            />
            <button type="submit" className="va-chat-send" disabled={chatBusy || !chatInput.trim()}>
              Send
            </button>
          </form>

          <footer className="va-hint">
            {MOD}K toggle · Esc collapse{voiceEnabled ? ` · ${MOD}⇧V voice · ${MOD}⇧M mute` : ''}
          </footer>
          </div>
          {showTheatre && (
            <aside className="va-theatre" aria-label="How the agent is thinking" data-nova-ignore="true">
              <header className="va-stage-head">
                <span>{thinkBusy ? 'Thinking' : 'How I thought'}</span>
                <button type="button" className="va-head-close" onClick={toggleWatch} aria-label="Hide the think stage">×</button>
              </header>
              {thinkPlayer(300)}
            </aside>
          )}
          </div>
        </div>
      )}
    </>
  );
}
